/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Star, X, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useRatePrompt } from "../lib/ratePrompt";
import { hideOnError } from "../lib/imgFallback";
import { toast } from "sonner";

const AXES = [
  { key: "insight",        label: "Insight" },
  { key: "accuracy",       label: "Accuracy" },
  { key: "communication",  label: "Communication" },
  { key: "ethics",         label: "Integrity" },
];

/**
 * ReviewPromptDialog — single-modal multi-axis review for a completed booking.
 *
 * Fetches the user's pending-review list on mount; shows the most recent one.
 * On 5-star submit, triggers the App Store rate-prompt flow (Apple / Google /
 * web modal) so we collect store ratings from genuinely happy seekers.
 *
 * Mount this once on the Wallet page (where bookings already live).
 */
export default function ReviewPromptDialog() {
  const [pending, setPending] = useState([]);
  const [current, setCurrent] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [axes, setAxes] = useState({});
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { maybePromptAfter } = useRatePrompt();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bookings/pending-review");
        const list = data?.pending || [];
        setPending(list);
        // Cool-down dismissals via sessionStorage so we don't haunt the user
        const dismissed = JSON.parse(sessionStorage.getItem("cauldron_review_dismissed") || "[]");
        const next = list.find((b) => !dismissed.includes(b.bookingId));
        if (next) setCurrent(next);
      } catch {
        /* noop */
      }
    })();
  }, []);

  const close = (markDismissed = true) => {
    if (markDismissed && current) {
      try {
        const dismissed = JSON.parse(sessionStorage.getItem("cauldron_review_dismissed") || "[]");
        dismissed.push(current.bookingId);
        sessionStorage.setItem("cauldron_review_dismissed", JSON.stringify(dismissed));
      } catch { /* noop */ }
    }
    setCurrent(null);
    setRating(0);
    setAxes({});
    setComment("");
  };

  const submit = async () => {
    if (!current) return;
    if (rating < 1) {
      toast.error("Pick a star rating to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        providerId: current.providerId,
        bookingId: current.bookingId,
        rating,
        comment: comment.trim() || undefined,
        axes: Object.keys(axes).length ? axes : undefined,
      });
      toast.success("Review submitted — thank you.");
      // If they loved it, trigger the App Store rate flow next.
      if (rating === 5) {
        setTimeout(() => { maybePromptAfter("five_star_review"); }, 800);
      }
      close(false);
      // Optimistically remove from the pending list and show the next one
      const remaining = pending.filter((b) => b.bookingId !== current.bookingId);
      setPending(remaining);
      if (remaining.length) {
        setTimeout(() => setCurrent(remaining[0]), 500);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="review-prompt-overlay"
    >
      <div className="parchment p-8 max-w-md w-full relative space-y-5" data-testid="review-prompt-dialog">
        <button
          onClick={() => close(true)}
          className="absolute top-3 right-3 text-foreground/50 hover:text-foreground"
          aria-label="Close"
          data-testid="review-prompt-close-btn"
        >
          <X size={18} strokeWidth={1.75} />
        </button>

        <div className="space-y-3 text-center">
          <div className="label-mystic text-secondary flex items-center justify-center gap-1.5">
            <Sparkles size={12} strokeWidth={1.75} /> How was your session?
          </div>
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full bg-secondary/15 border border-secondary/40 flex items-center justify-center font-serif text-base text-secondary overflow-hidden">
              {current.providerAvatarUrl ? (
                <img src={current.providerAvatarUrl} alt="" onError={hideOnError} className="w-full h-full object-cover" />
              ) : (
                (current.providerName?.[0] || "?").toUpperCase()
              )}
            </div>
            <div className="text-left">
              <div className="font-serif text-lg">{current.providerName}</div>
              {current.serviceTitle && (
                <div className="text-xs text-foreground/55">{current.serviceTitle}</div>
              )}
            </div>
          </div>
        </div>

        {/* Star rating */}
        <div className="flex justify-center gap-2" data-testid="review-prompt-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setRating(n)}
              className="p-1 transition-transform hover:scale-110"
              data-testid={`review-star-${n}`}
              aria-label={`${n} stars`}
            >
              <Star
                size={32}
                strokeWidth={1.5}
                className={(hover || rating) >= n ? "fill-secondary text-secondary" : "text-foreground/30"}
              />
            </button>
          ))}
        </div>

        {/* Multi-axis sliders — collapsed unless they've picked a star */}
        {rating > 0 && (
          <div className="space-y-2.5" data-testid="review-prompt-axes">
            {AXES.map((a) => (
              <div key={a.key} className="flex items-center justify-between gap-2">
                <div className="text-xs uppercase tracking-[0.16em] text-foreground/65">{a.label}</div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setAxes({ ...axes, [a.key]: n })}
                      className="p-0.5"
                      data-testid={`review-axis-${a.key}-${n}`}
                      aria-label={`${a.label} ${n}`}
                    >
                      <Star
                        size={16}
                        strokeWidth={1.5}
                        className={(axes[a.key] || 0) >= n ? "fill-secondary text-secondary" : "text-foreground/30"}
                      />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, 600))}
          placeholder="What was your experience like? (optional)"
          rows={3}
          className="w-full bg-background/30 border border-secondary/25 rounded-sm px-3 py-2 text-sm font-serif focus:outline-none focus:border-secondary resize-none"
          data-testid="review-prompt-comment"
        />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => close(true)}
            className="text-xs uppercase tracking-wider text-foreground/55 hover:text-foreground px-4 py-2"
            data-testid="review-prompt-later-btn"
          >
            Later
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || rating < 1}
            className="bg-primary text-primary-foreground px-6 py-2.5 rounded-sm uppercase tracking-wider text-xs font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors"
            data-testid="review-prompt-submit-btn"
          >
            {submitting ? "Sending…" : "Submit review"}
          </button>
        </div>
      </div>
    </div>
  );
}
